// MER-545: prior-period window bounds for KPI tile "vs prior" deltas.
//
// A widget opting in via `trend: vs_prior_period` gets a second KPI query
// over the window immediately BEFORE its own timeline window; the renderer
// turns (current, prior) into the "+X.X% vs prior" delta on KPITile.
//
// Pure date math lives here (node:test coverage in scripts/prior-window.test.mjs);
// queries.ts applies the returned bounds to the Supabase filter chain.
//
// Rules, mirroring applyTimeline's window semantics (queries.ts):
//   mode snapshot / no column   -> null (as-of values have no prior window)
//   period + this_month         -> null. The current window is a PARTIAL
//                                  month; comparing it against any complete
//                                  prior window is the A3 partial-vs-full
//                                  trap from Delta's fix plan. No delta
//                                  beats a systematically wrong one.
//   period + last_month         -> two calendar months back (complete vs
//                                  complete, comparable)
//   named trailing range (N d)  -> [today - 2N, today - N)
//   month:YYYY-MM (MER-692)     -> the previous calendar month, exact bounds;
//                                  null while the month is incomplete
//   anything else               -> null
//
// Serialization replicates queries.ts applyTimeline EXACTLY so the prior
// window's exclusive end always seams with the current window's start:
// trailing bounds use setDate + toISOString (UTC) like applyTimeline's
// cutoff; month bounds use local month parts for text period columns
// (monthKey) and toISOString for `_date` columns (monthBounds). A mixed
// convention here would drop or double-count the seam day's events.
//
// NOTE for authors: trend belongs on EVENT-grain sources (service_event
// rows dated by event_date). Period-bucketed tables (kpi_summary,
// route_kpis, financial_health) anchor rows to the bucket start, so a
// trailing window catches a partial current bucket against complete prior
// buckets; queries.ts refuses the prior query for those tables.

import {
  isCompleteMonth,
  monthBoundsFor,
  parseMonthToken,
  previousMonth,
} from "./calendarMonth.mjs";

const TRAILING_DAY_RANGES = {
  this_week: 7,
  last_week: 7,
  this_month: 31,
  last_month: 31,
  last_30_days: 30,
  last_90_days: 90,
  last_180_days: 180,
  ytd: 365,
  last_4_weeks: 28,
  last_4_months: 122,
  last_6_months: 183,
  last_12_months: 366,
  last_quarter: 92,
  last_year: 366,
};

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthBounds(column, offset, today) {
  const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 1);
  if (column.endsWith("_date")) {
    return { gte: isoDate(start), lt: isoDate(end) };
  }
  return { gte: monthKey(start), lt: monthKey(end) };
}

/**
 * Prior-window bounds for a widget timeline, or null when no honest prior
 * window exists (snapshot mode, missing column, partial current window,
 * unknown range).
 *
 * @param {object|null|undefined} timeline  widget.timeline ({column, range, mode})
 * @param {Date} today  injected for testability
 * @returns {{gte: string, lt: string} | null}
 */
export function priorWindowBounds(timeline, today) {
  if (!timeline || !timeline.column) return null;
  if (timeline.mode === "snapshot") return null;
  const column = String(timeline.column);

  // MER-692: an explicit calendar month compares against the calendar month
  // before it, bounded the same way applyTimeline bounds the current month
  // on these (event-grain) sources: gte first day, lt first day of the next,
  // with the `_date` suffix deciding ISO date vs bare YYYY-MM. The prior's
  // exclusive end is therefore the current window's start, byte for byte.
  // A month that is not yet complete (the one in progress, or a future one
  // typed into the URL) has no honest prior: it would be the partial-vs-full
  // A3 trap again, so no delta. Never a trailing window under a month.
  const month = parseMonthToken(timeline.range);
  if (month) {
    if (!isCompleteMonth(month, today)) return null;
    return monthBoundsFor(previousMonth(month), column);
  }

  if (timeline.mode === "period" && timeline.range === "this_month") {
    // applyTimeline windows the current query to the in-progress calendar
    // month, which is partial until the last day. No comparable prior.
    return null;
  }
  if (timeline.mode === "period" && timeline.range === "last_month") {
    return monthBounds(column, -2, today);
  }

  const days = TRAILING_DAY_RANGES[timeline.range ?? ""];
  if (!days) return null;
  const lt = new Date(today);
  lt.setDate(lt.getDate() - days);
  const gte = new Date(today);
  gte.setDate(gte.getDate() - 2 * days);
  return { gte: isoDate(gte), lt: isoDate(lt) };
}

/**
 * Percent delta for the KPI tile, or null when a delta would mislead:
 * either value missing, prior is zero (no baseline), or the swing is
 * absurd (>500% reads as a data artifact, not a trend; the A5 lesson
 * from Delta's ▲1921% tiles).
 */
export function deltaPct(current, prior) {
  if (typeof current !== "number" || typeof prior !== "number") return null;
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return null;
  if (prior === 0) return null;
  const pct = ((current - prior) / Math.abs(prior)) * 100;
  if (!Number.isFinite(pct) || Math.abs(pct) > 500) return null;
  return pct;
}

/* ------------------------------------------------------------------ */
/* MER-553: saying so, when there can be no arrow                      */
/* ------------------------------------------------------------------ */

/**
 * The chip beside the period when a tile asked for a vs-prior comparison and
 * cannot honestly have one. Short, because it shares an uppercase mono line
 * with the range and any scope note. DELTA_WITHHELD_NOTE is its sibling for
 * the feed-lag case (feedLag.mjs).
 */
export const TREND_NOT_COMPARABLE_NOTE = "no vs prior";

/**
 * The same fact in a sentence, for the tile's info panel.
 *
 * It names the MECHANISM rather than blaming a feed, because there is nothing
 * wrong with the feed. A definition or shape gap must never read as a missing
 * upload (the rule the MER-819 definition gate exists for).
 */
export const TREND_NOT_COMPARABLE_REASON =
  "This tile counts whole periods, and each row is stamped at the period's " +
  "start. A comparison would have to line up two whole periods of the same " +
  "length, and the earlier window behind the arrow is not lined up that way, " +
  "so a month still filling would be measured against a month that finished. " +
  "That reads as a fall when nothing has fallen. The tile shows the figure " +
  "and no arrow rather than an arrow it cannot stand behind.";

/**
 * MER-553: does this tile want a vs-prior arrow it cannot honestly have?
 *
 * The manifest says what the READER wants ("compare this with the period
 * before"). This says what we can deliver. Splitting the two is the whole
 * point: the want stays recorded on the tile, so the day the pipeline
 * computes bucket-aligned prior columns, the same tiles start showing real
 * deltas with no manifest edit, and until then the tile says why it cannot.
 *
 * What it must NOT become. MER-545 removed the trend from these tiles because
 * the trailing prior window caught complete buckets while the current window
 * caught the one in progress, printing a large fake negative on flat volume
 * for most of every month. Nothing here computes a delta, requests a prior
 * pass, or relaxes the refusal in queries.ts. It only supplies the sentence
 * that used to be missing when the arrow silently vanished.
 *
 * Snapshot tiles are not in scope. A balance has no prior window by its
 * nature, not by a shape gap, and a tile reading today's receivables has
 * nothing to say about last month's.
 *
 * @param {{trend?: string, isPeriodGrain?: boolean, isSnapshot?: boolean}} opts
 * @returns {{note: string, reason: string} | null}
 */
export function trendWithheld(opts = {}) {
  if (opts.trend !== "vs_prior_period") return null;
  if (opts.isSnapshot === true) return null;
  if (opts.isPeriodGrain !== true) return null;
  return { note: TREND_NOT_COMPARABLE_NOTE, reason: TREND_NOT_COMPARABLE_REASON };
}
